import React, { useEffect, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import useExternalCalendarEvents, { filterEventsForDate } from "../hooks/useExternalCalendarEvents";
import { getAllTasksForDate, toLocalDateStr } from "../utils/calendarUtils";
import { api } from "../api/client";
import { onCalendarPrefs } from "../utils/calendarPrefsEvent";
import EventProposalsBanner from "./EventProposalsBanner";
import { PageHeader, PageLayout } from "../ui";

// Fresh-eyes 16 (Carla, 2026-10-12): the Business Calendar page (/calendar) has the standard page
// header, "Calendar" in the My work group. `asPage` is passed only there; the same month grid is
// embedded in My tasks and the schedule view, which have their own page titles.
const CALENDAR_EYEBROW = { label: 'My work', color: 'group-my-work' };

function CalendarView({
  calendarDate,
  setCalendarDate,
  setSelectedDate,
  setShowCalendarModal,
  tileContent,
  tasks,
  asPage = false,
}) {
  const [calPrefs, setCalPrefs] = useState({ weekStartsOn: 1 });

  useEffect(() => {
    api('/api/calendar/preferences')
      .then(r => r.json())
      .then(d => setCalPrefs({ weekStartsOn: d.weekStartsOn ?? 1 }))
      .catch(() => {});
    // Decision 9d: a change saved in Settings applies at once.
    return onCalendarPrefs(d => setCalPrefs({ weekStartsOn: d.weekStartsOn ?? 1 }));
  }, []);

  // Fetch synced external events for a rolling 60-day window centred on today
  const monthStart = new Date(calendarDate);
  monthStart.setDate(1);
  monthStart.setHours(0, 0, 0, 0);
  const monthEnd = new Date(monthStart);
  monthEnd.setMonth(monthEnd.getMonth() + 1);
  const { events: syncedEvents } = useExternalCalendarEvents(monthStart.toISOString(), monthEnd.toISOString());

  function tasksForDate(date) {
    const dateStr = toLocalDateStr(date);
    const aideyTasks = getAllTasksForDate(tasks || [], [], dateStr, [], []);
    const extForDay  = filterEventsForDate(syncedEvents, dateStr);
    // Append synced events as lightweight objects so tileContent can count them
    return [...aideyTasks, ...extForDay.map(ev => ({ ...ev, _synced: true }))];
  }

  if (asPage) {
    return (
      <PageLayout data-page="calendar">
        <PageHeader eyebrow={CALENDAR_EYEBROW} title="Calendar" />
        {/* Aidey-detected events from emails: one slim line, the list in a panel. */}
        <EventProposalsBanner />
        <div className="calendar-container">
          <Calendar
            value={calendarDate}
            locale={calPrefs.weekStartsOn === 0 ? 'en-US' : 'en-GB'}
            tileContent={(props) => tileContent({ date: props.date, view: props.view }, tasksForDate(props.date))}
            onClickDay={(date) => {
              setCalendarDate(date);
              setSelectedDate(date);
              setShowCalendarModal(true);
            }}
          />
        </div>
      </PageLayout>
    );
  }

  return (
    <div style={{ padding: "1rem", display: "flex", flexDirection: "column", alignItems: "center", flexGrow: 1 }}>
      <div style={{ width: "100%", maxWidth: "1400px" }}>
        {/* Aidey-detected events from emails — shared banner (same as Family) */}
        <EventProposalsBanner />
        <div className="calendar-container">
          <Calendar
            value={calendarDate}
            locale={calPrefs.weekStartsOn === 0 ? 'en-US' : 'en-GB'}
            tileContent={(props) => tileContent({ date: props.date, view: props.view }, tasksForDate(props.date))}
            onClickDay={(date) => {
              setCalendarDate(date);
              setSelectedDate(date);
              setShowCalendarModal(true);
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default CalendarView;